import React, { useCallback, useEffect, useState } from 'react';
import { billingService, getErrorMessage, User } from '../services/api';
import type { AdminClassPlan } from '../types/api';
import { useToast } from './Toast';
import { formatDate } from '../utils/format';

const STUDENTS_PAGE_SIZE = 20;
// A free month (PLAN-18), as the Student plan's default grant (31 days, backend/app/api/billing.py).
const monthFromNow = () => new Date(Date.now() + 31 * 24 * 3600 * 1000).toISOString();

// Admins grant and end plans until Paddle checkout exists (docs/REQUIREMENTS.md PLAN-6, PLAN-9): a free month of the
// Student plan or of a class's Class plan, which users ask for by email (PLAN-18), or a semester of the Class plan for a
// professor who paid. The user is emailed (PLAN-19).
const PlansAdmin: React.FC<{ users: User[]; onChange: () => void }> = ({ users, onChange }) => {
  const toast = useToast();
  const [classes, setClasses] = useState<AdminClassPlan[]>([]);
  const [showAllStudents, setShowAllStudents] = useState(false);
  const loadClasses = useCallback(() => {
    billingService.listClasses().then(setClasses).catch((error) => toast.error(getErrorMessage(error)));
  }, [toast]);
  useEffect(loadClasses, [loadClasses]);

  const run = async (action: () => Promise<void>, done: string) => {
    try {
      await action();
      toast.success(done);
      onChange();
      loadClasses();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const hasStudentPlan = (u: User) => u.plan === 'student' && (!u.plan_period_end || new Date(u.plan_period_end) > new Date());
  const students = users.filter((u) => u.role === 'student');
  // Paying students first, then the rest; the long tail of free accounts is behind "Show all".
  const sortedStudents = [...students].sort((a, b) => Number(hasStudentPlan(b)) - Number(hasStudentPlan(a)));
  const visibleStudents = showAllStudents ? sortedStudents : sortedStudents.slice(0, STUDENTS_PAGE_SIZE);
  const endButton = 'btn btn-sm btn-danger-soft';
  const grantButton = 'btn btn-sm btn-secondary';

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">Plans</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Until online payment is connected: grant a free month when someone asks for one (the request email links here), or a plan when someone has paid. The user gets an email, with a copy to contact@.</p>
      </div>

      <div className="card overflow-hidden">
        <h3 className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800">Classes (Class plan, €890 / semester)</h3>
        <div className="relative overflow-x-auto">
          <table className="table min-w-[560px]">
            <thead><tr><th>Class</th><th>Teacher</th><th>Students</th><th>Plan</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id}>
                  <td className="font-medium !text-gray-900 dark:!text-white">{c.name}</td>
                  <td>{c.teacher}</td>
                  <td className="tabular-nums">{c.students}</td>
                  <td>{c.plan === 'class' ? <span className="badge badge-green normal-case tracking-normal">Class plan until {formatDate(c.plan_period_end)}</span> : <span className="badge badge-gray normal-case tracking-normal">Evaluation</span>}</td>
                  <td className="text-right !py-2">
                    {c.plan === 'class' ? (
                      <button className={endButton} onClick={() => run(() => billingService.setClassPlan(c.id, false), `"${c.name}" is in evaluation again`)}>End plan</button>
                    ) : (
                      <span className="inline-flex flex-wrap justify-end gap-2">
                        {/* PLAN-18: a free month asked for while online payment is pending; the teacher is emailed (PLAN-19). */}
                        <button className={grantButton} onClick={() => run(() => billingService.setClassPlan(c.id, true, monthFromNow(), true), `"${c.name}" has a free month of the Class plan`)}>Free month</button>
                        <button className={grantButton} onClick={() => run(() => billingService.setClassPlan(c.id, true), `"${c.name}" has the Class plan for a semester`)}>Activate (a semester)</button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card overflow-hidden">
        <h3 className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-800">Students (Student plan, €9 / month)</h3>
        <div className="relative overflow-x-auto">
          <table className="table min-w-[480px]">
            <thead><tr><th>Student</th><th>Plan</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {visibleStudents.map((u) => (
                <tr key={u.id}>
                  <td><span className="font-medium text-gray-900 dark:text-white">{u.full_name}</span> <span className="text-gray-500 dark:text-gray-400">({u.email})</span></td>
                  <td>{hasStudentPlan(u) ? <span className="badge badge-green normal-case tracking-normal">Student plan until {formatDate(u.plan_period_end)}</span> : <span className="badge badge-gray normal-case tracking-normal">Free</span>}</td>
                  <td className="text-right !py-2">
                    {hasStudentPlan(u) ? (
                      <button className={endButton} onClick={() => run(() => billingService.setStudentPlan(u.id, false), `${u.full_name} is on Free again`)}>End plan</button>
                    ) : (
                      <button className={grantButton} onClick={() => run(() => billingService.setStudentPlan(u.id, true, undefined, true), `${u.full_name} has a free month of the Student plan`)}>Grant a free month</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visibleStudents.length < sortedStudents.length && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 dark:border-gray-800 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
            <span>Showing {visibleStudents.length} of {sortedStudents.length}</span>
            <button onClick={() => setShowAllStudents(true)} className="btn btn-secondary btn-sm">Show All</button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PlansAdmin;
